import { inject, Service } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { IntegratorCreation } from '../../model/integrator.model';
import { MerchantCreation } from '../../model/merchant-details.model';
import { ApiService } from '../../services/api.service';
import { IntegratorService } from '../../services/integrator/integrator.service';
import { LogToastService } from '../../services/log-toast/log-toast.service';
import { MerchantService } from '../../services/merchant/merchant.service';

@Service()
export class RegistrationService {
  #merchantService = inject(MerchantService);
  #integratorService = inject(IntegratorService);
  #logToast = inject(LogToastService);
  #api = inject(ApiService);

  createMerchant(data: MerchantCreation) {
    return this.#merchantService.createMerchant(data).pipe(
      catchError((error) => {
        this.#logToast.error('MESSAGES.ERRORS.ERROR_CREATING_MERCHANT', error);
        return throwError(() => error);
      }),
      tap(() => {
        this.#logToast.success('MESSAGES.SUCCESS.SUCCESSFULLY_CREATED_MERCHANT_ACCOUNT', undefined, {
          logger: false,
          toast: true,
        });
      }),
    );
  }

  createIntegrator(data: IntegratorCreation): Observable<{ integratorId: number; userId: string }> {
    return this.#integratorService.createIntegrator(data).pipe(
      catchError((error) => {
        this.#logToast.error('MESSAGES.ERRORS.ERROR_CREATING_INTEGRATOR', error);
        return throwError(() => error);
      }),
      tap(() => {
        this.#logToast.success('MESSAGES.SUCCESS.SUCCESSFULLY_CREATED_INTEGRATOR_ACCOUNT', undefined, {
          logger: false,
          toast: true,
        });
      }),
    );
  }

  async getTermsAndConditions(apkPackageName: string): Promise<string> {
    try {
      const response = await this.#api.getBranding(apkPackageName);
      return response?.termsAndConditionsUrl;
    } catch (e) {
      this.#logToast.error('MESSAGES.ERRORS.FAILED_TO_GET_BRANDING', e, undefined, { logger: true, toast: false });
      return '';
    }
  }

  async checkUniqueness(
    email: string,
    mobileNumber: string | undefined,
    entityName: string,
    username: string,
  ): Promise<boolean> {
    try {
      const [emailAvailable, mobileNumberAvailable, merchantNameAvailable, usernameAvailable] =
        (await Promise.allSettled([
          this.#api.isEmailAvailable(email),
          mobileNumber ? this.#api.isMobileNumberAvailable(mobileNumber) : Promise.resolve({ available: true }),
          this.#api.isMerchantNameAvailable(entityName),
          this.#api.isUsernameAvailable(username),
        ])) as PromiseFulfilledResult<{ available: boolean }>[];

      if (!emailAvailable?.value?.available) {
        this.#logToast.error('MESSAGES.ERRORS.SIGN_UP_WITH_UNIQUE_EMAIL');
        return false;
      }

      if (!mobileNumberAvailable?.value?.available) {
        this.#logToast.error('MESSAGES.ERRORS.SIGN_UP_WITH_UNIQUE_PHONE_NUMBER');
        return false;
      }

      if (!usernameAvailable?.value?.available) {
        this.#logToast.error('MESSAGES.ERRORS.SIGN_UP_WITH_UNIQUE_USERNAME');
        return false;
      }

      if (!merchantNameAvailable?.value?.available) {
        this.#logToast.error('MESSAGES.ERRORS.SIGN_UP_WITH_UNIQUE_BUSINESS_NAME');
        return false;
      }

      return true;
    } catch (error) {
      this.#logToast.error('MESSAGES.ERRORS.FAILED_TO_VALID_UNIQUENESS', error);
      return false;
    }
  }
}
